import React, { useContext, useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Loader, Sparkles, X } from 'lucide-react';
import { useFormOptions } from 'features/ai-blog-writer/hooks/useFormOptions';
import { BlogContext } from 'features/ai-blog-writer/BlogContext';
import toast from 'react-hot-toast';
import blogService from 'features/ai-blog-writer/services/blogApi';
import { useLocation, useNavigate } from 'react-router-dom';
import TurndownService from 'turndown';
import { BlogContent } from 'features/ai-blog-writer/types';
import BlogDiscard from '../Modals/BlogDiscard';

interface EditPanelProps {
  onClose: () => void;
}

interface Language {
  name: string;
}

interface Tones {
  name: string;
}

interface Industry {
  name: string;
}

interface FormErrors {
  topic?: string;
  language?: string;
  img_number?: string;
  tone?: string;
  industry?: string;
  min_len?: string;
  max_len?: string;
}

export const convertToMarkdown = (htmlInput: string) => {
  try {
    const turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      emDelimiter: '_',
      bulletListMarker: '-',
      hr: '---',
    });

    // Add additional rules for better conversion
    turndownService.addRule('superscript', {
      filter: ['sup'],
      replacement: function (content) {
        return '^' + content;
      },
    });

    turndownService.addRule('subscript', {
      filter: ['sub'],
      replacement: function (content) {
        return '~' + content;
      },
    });

    const markdown = turndownService.turndown(htmlInput);

    return markdown;
  } catch (err) {
    console.log(err);
    return '';
  }
};

export function EditPanel({ onClose }: EditPanelProps) {
  const data = useFormOptions();
  const location = useLocation();
  const [errors, setErrors] = useState<FormErrors>({});

  const {
    handleGenerateBlog,
    blogContent,
    handleUpdateBlogContent,
    isGenerating,
  } = useContext(BlogContext);
  const navigate = useNavigate();
  const [buttonName, setButtonName] = useState('Apply');
  const [formData, setFormData] = useState({
    topic: String(blogContent?.title),
    language: String(blogContent?.settings.language),
    img_number: Number(blogContent?.settings.imageCount),
    tone: String(blogContent?.settings.toneOfVoice),
    industry: String(blogContent?.settings.industry),
    min_len: Number(blogContent?.settings.minLength),
    max_len: Number(blogContent?.settings.maxLength),
  });
  const searchParams1 = new URLSearchParams(location.search);
  const id = searchParams1.get('id');

  const validateEditForm = () => {
    const newErrors: FormErrors = {};
    const MAX_IMAGES = 5;

    // Topic validation
    if (!formData.topic.trim()) {
      newErrors.topic = 'Topic is required';
    } else if (formData.topic.length < 3) {
      newErrors.topic = 'Topic must be at least 3 characters long';
    }

    // Language validation
    if (!formData.language) {
      newErrors.language = 'Language is required';
    }

    // Image number validation
    if (formData.img_number < 0 || formData.img_number > MAX_IMAGES) {
      newErrors.img_number = `Number of images must be between 0 and ${MAX_IMAGES}`;
    }

    // Tone validation
    if (!formData.tone) {
      newErrors.tone = 'Tone is required';
    }

    // Industry validation
    if (!formData.industry) {
      newErrors.industry = 'Industry is required';
    }

    // Length validation
    if (formData.min_len >= formData.max_len) {
      newErrors.min_len = 'Minimum length must be less than maximum length';
      newErrors.max_len = 'Maximum length must be greater than minimum length';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormValidation = () => {
    return (
      formData.topic &&
      formData.min_len &&
      formData.max_len &&
      formData.language &&
      formData.tone &&
      formData.industry &&
      formData.img_number >= 0
    );
  };

  const handleSaveChanges = async () => {
    if (!validateEditForm()) {
      toast.error('Please fix all errors before saving changes');
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const blogId = searchParams.get('id');
    const markDownContent = convertToMarkdown(String(blogContent?.content));

    if (blogId) {
      try {
        await blogService.updateBlog(Number(blogId), {
          title: String(blogContent?.title),
          context: String(markDownContent),
          file_name: String(blogContent?.title),
        });
        navigate(`?id=${blogId}`);
        toast.success('Blog Updated Successfully');

        onClose();
      } catch (err) {
        console.error(err);
        toast.error('Failed to save changes. Please try again.');
      }
    }
  };

  const handleApply = async () => {
    if (isGenerating) return;

    if (!validateEditForm()) {
      toast.error('Please fix the form errors before applying changes');
      return;
    }

    if (!handleFormValidation()) {
      toast.error('Please fill in all the required fields');
      return;
    }

    try {
      setButtonName('Applying...');
      //@ts-expect-error irreleventTypeIssue
      handleUpdateBlogContent((prevBlogContent: BlogContent) => {
        return {
          content: prevBlogContent.content,
          title: String(formData.topic),
          settings: {
            minLength: String(formData.min_len),
            maxLength: String(formData.max_len),
            language: String(formData.language),
            toneOfVoice: String(formData.tone),
            industry: String(formData.industry),
            imageCount: Number(formData.img_number),
          },
        };
      });

      await handleGenerateBlog(formData);
      toast.success('Blog generated successfully');
      onClose();
    } catch (e) {
      toast.error('Failed to generate blog. Please try again');
      console.error(e);
    } finally {
      setButtonName('Apply');
    }
  };

  const transformer = () => {
    console.log('transparency edit panel');
    const data = {
      topic: String(blogContent?.title),
      language: String(blogContent?.settings.language),
      img_number: Number(blogContent?.settings.imageCount),
      tone: String(blogContent?.settings.toneOfVoice),
      industry: String(blogContent?.settings.industry),
      min_len: Number(blogContent?.settings.minLength),
      max_len: Number(blogContent?.settings.maxLength),
    };
    setFormData(data);
  };

  useEffect(() => {
    transformer();
  }, [isGenerating]);

  return (
    <div className="h-full flex flex-col bg-background rounded-lg p-0 m-0 shadow-lg">
      {/* Fixed Header */}
      <div className="flex-none px-6 py-2 border-b">
        <div className="flex items-center justify-between text-primary">
          <h2 className="text-base font-normal ">EDIT</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Scrollable Settings */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          <div className="space-y-4 text-text-primary">
            <div>
              <label className="text-[13px] font-medium mb-2 block">
                Topic
              </label>
              <Input
                placeholder="Enter topic here"
                value={formData.topic}
                onChange={(e) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    topic: e.target.value,
                  }));
                }}
                className={errors.topic ? 'border-red-500' : ''}
              />
              {errors.topic && (
                <p className="text-red-500 text-xs mt-1">{errors.topic}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-base font-medium ">Minimum Length</label>
              <Select
                value={String(formData.min_len)}
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    min_len: Number(value),
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full border rounded-md ${errors.min_len ? 'border-red-500' : ''}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="300">300 words</SelectItem>
                  <SelectItem value="500">500 words</SelectItem>
                  <SelectItem value="700">700 words</SelectItem>
                </SelectContent>
              </Select>
              {errors.min_len && (
                <p className="text-red-500 text-xs mt-1">{errors.min_len}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-base font-medium ">Maximum Length</label>
              <Select
                value={String(formData.max_len)}
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    max_len: Number(value),
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full  border rounded-md ${errors.max_len ? 'border-red-500' : ''}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="500">500 words</SelectItem>
                  <SelectItem value="700">700 words</SelectItem>
                  <SelectItem value="1000">1000 words</SelectItem>
                </SelectContent>
              </Select>
              {errors.max_len && (
                <p className="text-red-500 text-xs mt-1">{errors.max_len}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-base font-medium ">Language</label>
              <Select
                value={formData.language}
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    language: value,
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full  border rounded-md ${errors.language ? 'border-red-500' : ''}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data?.data?.languages.map(
                    (item: Language, index: number) => (
                      <SelectItem key={index} value={item?.name}>
                        {item?.name}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              {errors.language && (
                <p className="text-red-500 text-xs mt-1">{errors.language}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-base font-medium">Tone of Voice</label>
              <Select
                value={formData.tone}
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    tone: value,
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full  border rounded-md ${errors.tone ? 'border-red-500' : ''}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data?.data?.tones.map((item: Tones, index: number) => (
                    <SelectItem key={index} value={item?.name}>
                      {item?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.tone && (
                <p className="text-red-500 text-xs mt-1">{errors.tone}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-base font-medium ">Industry</label>
              <Select
                value={formData.industry}
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    industry: value,
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full  border rounded-md ${errors.industry ? 'border-red-500' : ''}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data?.data?.industries.map(
                    (item: Industry, index: number) => (
                      <SelectItem key={index} value={item?.name}>
                        {item?.name}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              {errors.industry && (
                <p className="text-red-500 text-xs mt-1">{errors.industry}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-base font-medium ">No. of Images</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={formData.img_number}
                  onChange={(e) => {
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      img_number: Number(e.target.value),
                    }));
                  }}
                  className={`flex-1  border rounded-md ${errors.img_number ? 'border-red-500' : ''}`}
                />
                <Button
                  size="sm"
                  variant="customSecondary"
                  className="bg-background text-[24px] text-primary"
                  onClick={() =>
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      img_number: Math.min(5, prevFormData.img_number + 1),
                    }))
                  }
                >
                  +
                </Button>
                <Button
                  size="sm"
                  variant="customSecondary"
                  className="bg-background text-[28px] text-primary"
                  onClick={() =>
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      img_number: Math.max(0, prevFormData.img_number - 1),
                    }))
                  }
                >
                  -
                </Button>
              </div>
              {errors.img_number && (
                <p className="text-red-500 text-xs mt-1">{errors.img_number}</p>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="flex-none border-t border-primary rounded-lg  p-6 shadow-[0_-2px_4px_rgba(0,0,0,0.1)]">
          {/* Primary Actions */}
          <div className="space-y-3 mb-4">
            <Button
              variant={'customSecondary'}
              className="w-full bg-secondary "
              onClick={handleApply}
              disabled={buttonName !== 'Apply'}
            >
              {buttonName === 'Apply' ? (
                <Sparkles className="w-4 h-4 mr-2" />
              ) : (
                <div className="">
                  <Loader className="animate-spin w-4 h-4 mr-2" />
                </div>
              )}
              {buttonName}
            </Button>
          </div>

          {/* Save Actions */}
          <div className="space-y-3">
            {id && (
              <Button
                variant={'customSecondary'}
                className="w-full bg-secondary"
                onClick={handleSaveChanges}
              >
                Save Changes
              </Button>
            )}
            <BlogDiscard onClose={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditPanel;
